import { Info, CreditCard, Coins, Crown } from "lucide-react";

interface BookingMethodNoticeProps {
  allowedMethods?: string[];
}

const BookingMethodNotice = ({ allowedMethods }: BookingMethodNoticeProps) => {
  // Default to all methods if not specified
  const methods = allowedMethods || ["stripe", "credits", "unlimited"];
  
  // If all methods are allowed, don't show any notice
  if (methods.length >= 3) return null;
  
  // Build the notice message based on allowed methods
  const getNoticeMessage = () => {
    const hasStripe = methods.includes("stripe");
    const hasCredits = methods.includes("credits");
    const hasUnlimited = methods.includes("unlimited");
    
    if (methods.length === 1) {
      if (hasCredits) return "This event can only be booked using Nimi credits.";
      if (hasStripe) return "This event can only be booked using card payment.";
      if (hasUnlimited) return "This event is only available for Unlimited members.";
    }
    
    if (methods.length === 2) {
      if (hasCredits && hasUnlimited) return "This event can be booked using credits or Unlimited membership.";
      if (hasStripe && hasUnlimited) return "This event can be booked using card payment or Unlimited membership.";
      if (hasStripe && hasCredits) return "This event can be booked using card payment or credits.";
    }
    
    return null;
  };
  
  const getNoticeTitle = () => {
    const hasStripe = methods.includes("stripe");
    const hasCredits = methods.includes("credits");
    const hasUnlimited = methods.includes("unlimited");
    
    if (methods.length === 1) {
      if (hasCredits) return "Credits Only";
      if (hasStripe) return "Card Payment Only";
      if (hasUnlimited) return "Unlimited Members Only";
    }
    
    const parts = [];
    if (hasCredits) parts.push("Credits");
    if (hasStripe) parts.push("Card");
    if (hasUnlimited) parts.push("Unlimited");
    
    return parts.join(" or ") + " Only";
  };
  
  const message = getNoticeMessage();
  if (!message) return null;
  
  const getIcon = () => {
    if (methods.length === 1) {
      if (methods.includes("credits")) return <Coins className="h-4 w-4 text-amber-600" />;
      if (methods.includes("stripe")) return <CreditCard className="h-4 w-4 text-blue-600" />;
      if (methods.includes("unlimited")) return <Crown className="h-4 w-4 text-purple-600" />;
    }
    return <Info className="h-4 w-4 text-muted-foreground" />;
  };
  
  return (
    <div className="bg-muted/50 border border-border rounded-lg p-4 mb-4">
      <div className="flex items-start gap-3">
        {getIcon()}
        <div>
          <p className="font-medium text-sm">{getNoticeTitle()}</p>
          <p className="text-sm text-muted-foreground mt-0.5">{message}</p>
        </div>
      </div>
    </div>
  );
};

export default BookingMethodNotice;
